import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaPlus, FaMoneyBillWave, FaWallet, FaHandHoldingUsd, FaArrowLeft,
} from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import DemandeAvance from './DemandeAvance';
import DemandeAcompte from './DemandeAcompte';
import DemandePret from './DemandePret';
import SyntheseAvances from './SyntheseAvances';
import TableauRemboursements from './TableauRemboursements';
import { formaterMois, formaterMontant } from './formatAvances';
import PiecesJointes from './PiecesJointes';
import { AnnulerDemandeArgent, ModifierDemandeArgent } from './RetoucheDemandeArgent';

/**
 * Destination « Avances et prêts » de l'espace collaborateur (Yros/Kavero#136,
 * #410).
 *
 * Le salarié veut savoir trois choses, dans cet ordre : combien il doit encore,
 * ce qui partira de son prochain bulletin, et où en sont ses demandes. La page
 * suit cet ordre (recette du 21/09/2026) : trois chiffres en haut, une section
 * « Échéancier » avec une carte par remboursement en cours, puis la liste de
 * ses demandes — en cartes, parce qu'une table à six colonnes ne tient pas sur
 * le téléphone qu'utilise l'espace collaborateur.
 *
 * Un seul point d'entrée pour demander : « Nouvelle demande », puis un choix où
 * chaque type dit ce qu'il est. Trois boutons côte à côte se répétaient sans
 * aider à choisir.
 *
 * **Le filtrage par type n'est pas cosmétique.** Le socle rend toutes les
 * demandes du salarié dans la même enveloppe, congés compris : sans ce filtre,
 * cette destination afficherait des congés sous un titre qui parle d'argent.
 *
 * Le motif de refus est affiché tel qu'Odoo le rend : c'est la seule chose qui
 * dise au salarié pourquoi sa demande a été refusée.
 */

// Les trois types que cette destination réunit, dans l'ordre où ils engagent
// le salarié : l'acompte ne crée aucune dette, l'avance et le prêt si.
const TYPES_ARGENT = ['acompte', 'avance', 'pret'];

const FORMULAIRES = {
  acompte: DemandeAcompte,
  avance: DemandeAvance,
  pret: DemandePret,
};

const ICONES = {
  acompte: FaWallet,
  avance: FaMoneyBillWave,
  pret: FaHandHoldingUsd,
};

const COULEUR_ETAT = {
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
  annulee: 'bg-slate-100 text-slate-600',
};
const COULEUR_ETAT_DEFAUT = 'bg-yellow-100 text-yellow-800';

/**
 * Ce que la demande a à dire de sa période, selon ce qu'elle est.
 *
 * L'avance porte un mois de retenue, le prêt une première et une dernière
 * échéance, l'acompte rien du tout — il porte sur le mois en cours, et lui
 * inventer une date laisserait croire à un choix que le salarié n'a pas fait.
 */
function formaterPeriode(demande, locale, t) {
  const contenu = demande.contenu || {};
  if (demande.type === 'avance') {
    return formaterMois(contenu.mois_retenue, locale);
  }
  if (demande.type === 'pret' && contenu.debut && contenu.fin) {
    return `${formaterMois(contenu.debut, locale)} — ${formaterMois(contenu.fin, locale)}`;
  }
  return t('mesAvances.sansPeriode');
}

/** « 21 sept. 2026 » à partir de « 2026-09-21 09:40:43 ». */
function formaterDate(valeur, locale) {
  if (typeof valeur !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(valeur)) return '—';
  const date = new Date(`${valeur.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

// Littérale pour que Tailwind la génère : la demande, sa période, son dépôt, son état.
const GRILLE_DEMANDES = 'md:grid-cols-[minmax(0,1.6fr)_minmax(0,1.6fr)_minmax(0,1fr)_auto]';

function LigneDemande({ demande, locale, onRetoucher }) {
  const { t } = useTranslation();
  const Icone = ICONES[demande.type] || FaMoneyBillWave;
  return (
    <div
      role="row"
      data-testid={`mes-avances-ligne-${demande.id}`}
      className={`grid grid-cols-2 gap-x-4 gap-y-2 px-4 py-3 sm:px-5 md:items-center ${GRILLE_DEMANDES}`}
    >
      <div role="cell" className="col-span-2 md:col-span-1 flex items-center gap-3 min-w-0">
        <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
          <Icone className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="font-medium text-slate-900">
            {t(`mesAvances.types.${demande.type}`, { defaultValue: demande.type })}
          </p>
          <p className="text-sm text-slate-500 tabular-nums">{formaterMontant(demande.contenu?.montant, locale)}</p>
        </div>
      </div>
      <div role="cell" className="min-w-0">
        <span className="block text-xs text-slate-500 md:hidden">{t('mesAvances.colPeriode')}</span>
        <span data-testid={`mes-avances-periode-${demande.id}`} className="text-sm text-slate-700 capitalize">
          {formaterPeriode(demande, locale, t)}
        </span>
      </div>
      <div role="cell" className="min-w-0">
        <span className="block text-xs text-slate-500 md:hidden">{t('mesAvances.colonnes.depot')}</span>
        <span className="text-sm text-slate-700 tabular-nums">{formaterDate(demande.date_demande, locale)}</span>
      </div>
      <div role="cell" className="flex md:justify-end">
        <span
          className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
            COULEUR_ETAT[demande.etat] || COULEUR_ETAT_DEFAUT
          }`}
        >
          {t(`mesAvances.etats.${demande.etat}`, { defaultValue: demande.etat })}
        </span>
      </div>
      {(demande.contenu?.justificatifs || []).length > 0 && (
        <div role="cell" className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-xs text-slate-500">{t('piecesJointes.liste')}</span>
          <PiecesJointes demande={demande} />
        </div>
      )}
      {/* Yros/Kavero#492 : tant que personne n'a décidé, le salarié la retouche. */}
      {demande.modifiable && (
        <div role="cell" className="col-span-2 md:col-span-4 flex flex-wrap gap-2">
          <button type="button" data-testid={`mes-avances-modifier-${demande.id}`}
            onClick={() => onRetoucher(demande, 'modifier')}
            className="px-3 py-1.5 text-sm rounded-lg border border-[--color-primary] text-[--color-primary] hover:bg-[--color-primary] hover:text-white transition-colors">
            {t('retoucheDemande.modifier')}
          </button>
          <button type="button" data-testid={`mes-avances-annuler-${demande.id}`}
            onClick={() => onRetoucher(demande, 'annuler')}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
            {t('retoucheDemande.annulerDemande')}
          </button>
        </div>
      )}
      {demande.motif_refus && (
        <p role="cell" className="col-span-2 md:col-span-4 text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          <span className="font-medium">{t('mesAvances.colMotif')} : </span>
          {demande.motif_refus}
        </p>
      )}
    </div>
  );
}

function MesAvances() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [choixOuvert, setChoixOuvert] = useState(false);
  const [typeOuvert, setTypeOuvert] = useState(null);
  const [deposees, setDeposees] = useState(0);
  const [demandes, setDemandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  // { demande, action: 'modifier' | 'annuler' } ou null.
  const [retouche, setRetouche] = useState(null);

  // C'est la dernière requête lancée qui fait foi : le double montage de
  // `StrictMode` et un rechargement parti pendant qu'un autre est en vol
  // rendent leurs réponses dans n'importe quel ordre (même garde que la
  // supervision RH).
  const derniereRequete = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniereRequete.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await apiService.listerDemandesRh();
      if (numero !== derniereRequete.current) return;
      const toutes = reponse?.demandes || [];
      setDemandes(toutes.filter((demande) => TYPES_ARGENT.includes(demande.type)));
      setErreur(null);
    } catch (e) {
      if (numero !== derniereRequete.current) return;
      signalerErreur(e, 'MesAvances.charger');
      setDemandes([]);
      setErreur(t('mesAvances.erreurGenerique'));
    } finally {
      if (numero === derniereRequete.current) setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger, deposees]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg sm:text-xl font-bold text-slate-800">{t('mesAvances.titre')}</h1>
        {!choixOuvert && !typeOuvert && (
          <button
            type="button"
            data-testid="espace-nouvelle-demande-argent"
            onClick={() => setChoixOuvert(true)}
            className="flex items-center gap-2 bg-[--color-primary] hover:bg-[--bg-hover] text-white px-4 py-2 rounded-lg shadow transition"
          >
            <FaPlus aria-hidden="true" />
            <span>{t('mesAvances.nouvelleDemande')}</span>
          </button>
        )}
      </div>

      {choixOuvert && !typeOuvert && (
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-md border border-gray-100 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base sm:text-lg font-semibold">{t('mesAvances.choisirType')}</h2>
            <button
              type="button"
              data-testid="espace-annuler-demande"
              onClick={() => setChoixOuvert(false)}
              className="text-sm text-slate-600 hover:text-slate-900 underline"
            >
              {t('mesAvances.annuler')}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {TYPES_ARGENT.map((type) => {
              const Icone = ICONES[type];
              return (
                <button
                  key={type}
                  type="button"
                  data-testid={`espace-ouvrir-${type}`}
                  onClick={() => setTypeOuvert(type)}
                  className="text-left p-4 rounded-xl border border-slate-200 hover:border-[--color-primary] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[--color-primary] transition"
                >
                  <span className="flex items-center gap-2 font-semibold text-slate-800">
                    <Icone className="text-[--color-primary]" aria-hidden="true" />
                    {t(`mesAvances.choix.${type}.titre`)}
                  </span>
                  <span className="block mt-1 text-sm text-slate-600">
                    {t(`mesAvances.choix.${type}.description`)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {typeOuvert && (
        <div className="space-y-2">
          <button
            type="button"
            data-testid="espace-changer-type"
            onClick={() => setTypeOuvert(null)}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <FaArrowLeft aria-hidden="true" />
            {t('mesAvances.changerType')}
          </button>
          {React.createElement(FORMULAIRES[typeOuvert], {
            onDepot: () => setDeposees((n) => n + 1),
          })}
        </div>
      )}

      {erreur && (
        <p
          data-testid="mes-avances-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {erreur}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : (
        !erreur && (
          <>
            <SyntheseAvances demandes={demandes} locale={locale} />
            <TableauRemboursements demandes={demandes} locale={locale} />

            <section
              aria-labelledby="titre-demandes"
              className="bg-white rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="px-4 py-3 sm:px-5 border-b border-slate-200">
                <h2 id="titre-demandes" className="text-sm sm:text-base font-semibold text-slate-900">
                  {t('mesAvances.demandesTitre')}
                  <span className="ml-2 text-sm font-normal text-slate-500 tabular-nums">{demandes.length}</span>
                </h2>
              </div>
              {demandes.length === 0 ? (
                <p data-testid="mes-avances-vide" className="px-5 py-10 text-center text-sm text-slate-500">
                  {t('mesAvances.vide')}
                </p>
              ) : (
                <div role="table" aria-label={t('mesAvances.demandesTitre')}>
                  <div
                    role="row"
                    className={`hidden md:grid gap-x-4 px-5 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 ${GRILLE_DEMANDES}`}
                  >
                    <div role="columnheader">{t('mesAvances.colonnes.demande')}</div>
                    <div role="columnheader">{t('mesAvances.colPeriode')}</div>
                    <div role="columnheader">{t('mesAvances.colonnes.depot')}</div>
                    <div role="columnheader" className="text-right">{t('mesAvances.colEtat')}</div>
                  </div>
                  <div role="rowgroup" className="divide-y divide-slate-100">
                    {demandes.map((demande) => (
                      <LigneDemande key={demande.id} demande={demande} locale={locale}
                        onRetoucher={(cible, action) => setRetouche({ demande: cible, action })} />
                    ))}
                  </div>
                </div>
              )}
            </section>
          </>
        )
      )}

      {retouche?.action === 'modifier' && (
        <ModifierDemandeArgent demande={retouche.demande} onFermer={() => setRetouche(null)}
          onModifiee={() => { setRetouche(null); charger(); }} />
      )}
      {retouche?.action === 'annuler' && (
        <AnnulerDemandeArgent demande={retouche.demande} onFermer={() => setRetouche(null)}
          onAnnulee={() => { setRetouche(null); charger(); }} />
      )}
    </div>
  );
}

export default MesAvances;
